/**
 * Romanian message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: tu (informal, locked in l10n/locales.md).
 *
 * Platform terms follow Romanian UI conventions (setări, asociere for
 * pairing, oglindire for notification mirroring, închide for dismiss,
 * conversație for conversation, receptor for a receiving surface).
 * "Glance" is the untranslated feature brand name (glossary D1); "clip"
 * renders the D2 loanword verbatim (native Latin script in Romanian).
 * "Nu deranjați" uses Android/Chrome-ro's OS feature label for the DND
 * feature name; the imperative body copy stays in the tu register.
 *
 * Plural notes: Romanian is a {one, few, other} CLDR locale — every
 * count-bearing `selectPlural("ro", …)` supplies all three forms. Romanian
 * needs the `few` category for the "de" construction: few (2–19) is a bare
 * "%d notificări", other (20+) requires "%d de notificări". `one` covers
 * exactly 1, so the singular form is safe there.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Conectat",
    statusReconnecting: "Reconectare…",
    statusDisconnected: "Deconectat",
    openSettings: "Deschide setările",
    pairedFallbackDevice: "telefonul tău",
    publicModeToggleEnable: "Ascunde conținutul notificărilor",
    publicModeToggleDisable: "Afișează conținutul notificărilor",
  },

  notifications: {
    replyAriaLabel: "Răspunde",
    replyPlaceholder: "Răspunde…",
    replySend: "Trimite",
    sent: "Trimis",
    failedToSend: "Trimiterea a eșuat",
    unread: "Necitit",
    dismiss: "Închide",
    notificationImageAlt: "Imagine din notificare",
  },

  conversations: {
    replyAriaLabel: "Răspunde",
    replyPlaceholder: "Răspunde…",
    replySend: "Trimite",
    sent: "Trimis",
    failedToSend: "Trimiterea a eșuat",
    dismiss: "Închide conversația",
    messagesListLabel: "Mesaje în această conversație",
    deviceOwner: "Tu",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Niciun mesaj necitit"
        : `${count} ${selectPlural("ro", count, {
            one: "mesaj necitit",
            few: "mesaje necitite",
            other: "de mesaje necitite",
          })}`,
  },

  list: {
    today: "Azi",
    yesterday: "Ieri",
    searchNotifications: "Caută notificări",
    clearSearch: "Șterge căutarea",
    clearAll: "Șterge tot",
    markAllRead: "Marchează tot ca citit",
    groupCheckboxLabel: "Grupează",
    noNotificationsTitle: "Nicio notificare încă",
    noNotificationsDesc: "Mesajele noi oglindite de pe telefon vor apărea aici.",
    noMatchesTitle: "Nicio potrivire",
    notificationsList: "Notificări",
    privacyAppList: "Notificări după aplicație",
    clear: "Șterge",
    clearMatches: (count: number): string =>
      `Șterge ${count} ${selectPlural("ro", count, {
        one: "potrivire",
        few: "potriviri",
        other: "de potriviri",
      })}`,
    noMatchesDesc: (query: string): string => `Nicio notificare nu se potrivește cu „${query}”.`,
    appNotificationsGroup: (appName: string): string => `Notificări ${appName}`,
    dismissAllFrom: (appName: string): string => `Închide tot de la ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("ro", count, {
              one: "mesaj",
              few: "mesaje",
              other: "de mesaje",
            })
          : kind === "calls"
            ? selectPlural("ro", count, {
                one: "apel",
                few: "apeluri",
                other: "de apeluri",
              })
            : selectPlural("ro", count, {
                one: "notificare",
                few: "notificări",
                other: "de notificări",
              });
      return `${count} ${noun}`;
    },
    kbdDismiss: "închide",
    kbdNavigate: "navighează",
    kbdOrSeparator: "sau",
  },

  settings: {
    back: "Înapoi la notificări",
    settings: "Setări",
    appearance: "Aspect",
    theme: "Temă",
    themeAuto: "Automată",
    themeLight: "Luminoasă",
    themeDark: "Întunecată",
    language: "Limbă",
    languageSystemDefault: "Limba sistemului",
    notificationsSection: "Notificări",
    osNotifications: "Notificări de sistem",
    callNotifications: "Notificări de apel",
    privacySection: "Confidențialitate",
    analyticsToggle: "Statistici de utilizare anonime",
    analyticsToggleDescription: "Doar numărători și repere — niciodată notificările tale.",
    connection: "Conexiune",
    status: "Stare",
    statusConnected: "Conectat",
    statusReconnecting: "Reconectare…",
    statusDisconnected: "Deconectat",
    disconnectFromPhone: "Deconectează de la telefon",
    disconnectConfirm: "Sigur? Aceasta va anula asocierea dispozitivului tău.",
    yesDisconnect: "Da, deconectează",
    cancel: "Anulează",
    aboutAndSupport: "Despre și asistență",
    version: "Versiune",
    encryption: "Criptare",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Ai nevoie de ajutor? Scrie la __EMAIL__",
    disconnectFailed: "Deconectarea a eșuat — încearcă din nou",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Deconectat, dar curățarea locală a eșuat: ${errorMsg}. Reîncarcă pagina.`,
    disconnectLocalCleanupFailed: "Deconectat, dar curățarea locală a eșuat. Reîncarcă pagina.",
  },

  pairing: {
    enterCodeInstruction: "Introdu acest cod în aplicația Reflecto",
    pairedSuccessfully: "Asociere reușită!",
    pairingFailed: "Asocierea a eșuat",
    requestingPairingCode: "Se solicită codul de asociere…",
    codeExpired: "Codul a expirat",
    tryAgain: "Încearcă din nou",
    failedToStartPairing: "Asocierea nu a putut fi începută",
    needAppText: "Ai nevoie de aplicația Android? Scrie la __EMAIL__ pentru acces.",
  },

  dnd: {
    title: "Nu deranjați",
    description: "Întrerupe notificările pe acest dispozitiv în următoarea oră.",
    snooze1h: "Amână 1 oră",
    snoozeActiveUntil: (formattedTime: string): string => `Activ până la ${formattedTime}`,
    cancelSnooze: "Anulează",
  },

  connectors: {
    title: "Servicii conectate",
    emptyBody:
      "Niciun serviciu conectat încă. Conectează ChatGPT sau Claude din setările lor de conector, apoi aprobă codul pe telefon.",
    scopeSend: "Poate trimite notificări",
    added: (date: string): string => `Adăugat ${date}`,
    lastUsed: (relative: string): string => `Folosit ultima dată ${relative}`,
    neverUsed: "Nefolosit",
    revoke: "Deconectează",
    revokeConfirm: (label: string): string => `Deconectezi ${label}? Va pierde accesul imediat.`,
    revokeAction: "Deconectează",
    revokeConfirmLabel: (label: string): string => `Deconectează ${label}`,
    cancel: "Anulează",
    loadError: "Serviciile conectate nu au putut fi încărcate. Încearcă din nou.",
    revokeError: "Serviciul nu a putut fi deconectat. Încearcă din nou.",
    retry: "Reîncearcă",
  },

  feedback: {
    title: "Trimite feedback",
    close: "Închide",
    messageLabel: "Mesajul tău",
    messagePlaceholder: "Ai găsit un bug, ai o idee sau ceva nu e în regulă? Spune-ne.",
    categoryLabel: "Categorie",
    categoryBug: "Bug",
    categoryIdea: "Idee",
    categoryOther: "Altele",
    emailLabel: "E-mail (opțional, doar dacă vrei un răspuns)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Nu te vom adăuga la nicio listă.",
    disclosure:
      "Mesajul tău este trimis către serverul Reflecto și înregistrat în sistemul nostru intern de urmărire a problemelor. Atașăm versiunea aplicației și un ID de dispozitiv atribuit la asociere, pentru a putea corela rapoartele. Nu vedem notificările, contactele sau alte date personale ale tale. E-mailul este opțional — oferă-l doar dacă vrei un răspuns. Nu te adăugăm la nicio listă.",
    submit: "Trimite feedback",
    sending: "Se trimite…",
    success: "Mulțumim — este citit de un om, de obicei în 24 de ore",
    error: "Trimiterea a eșuat. Încearcă din nou.",
    done: "Gata",
    sendFeedback: "Trimite feedback",
  },

  glance: {
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Închide",
    aiSummaryTag: "Rezumat AI",
    errorAi: "Momentan nu s-a putut genera un rezumat.",
    retryCta: "Încearcă din nou",
    downloadHeading: "Primește rezumate AI",
    downloadBody:
      "Recuperează ce ai ratat cu un rezumat AI pe dispozitiv. Rulează în întregime pe dispozitivul tău.",
    downloadCta: "Descarcă modelul",
    downloadPrivacyNote: "O descărcare unică. Serverul nu îți vede niciodată notificările.",
    downloadingHeading: "Se descarcă modelul…",
    caughtUp: "Ești la zi — nimic nu îți cere atenția acum.",
    needsYou: "Necesită atenție",
    quiet: "Altele",
    scanning: (pending: number): string => `Se rezumă ${pending}…`,
    jump: "Deschide notificarea",
    affordanceReply: "Răspunde",
    affordanceOpen: "Deschide",
    affordanceCopyOtp: "Copiază codul",
    buttonLabel: "Glance",
  },

  update: {
    title: "Actualizare disponibilă",
    description: "O versiune nouă de Reflecto este gata. Reîncarcă pentru a actualiza.",
    reload: "Reîncarcă",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Am denumit acest dispozitiv „${name}”.`,
    cardLooksGood: "Arată bine",
    cardEdit: "Editează",
    cardAriaLabel: "Confirmă numele dispozitivului",
    renameTitle: "Redenumește acest dispozitiv",
    renameLabel: "Numele dispozitivului",
    renameSave: "Salvează",
    renameCancel: "Anulează",
    renameFailed: "Redenumirea nu a reușit. Încearcă din nou.",
  },

  collision: {
    title: (name: string): string => `Ai deja un „${name}”`,
    subtitle: "Cum să îl numim pe acesta?",
    chipWork: (name: string): string => `${name} de serviciu`,
    chipHome: (name: string): string => `${name} de acasă`,
    chipPersonal: (name: string): string => `${name} personal`,
    customLabel: "Nume personalizat",
    save: "Salvează",
    useAnyway: "Folosește totuși numele original",
    saveFailed: "Numele nou nu a putut fi salvat. Încearcă din nou.",
    ariaLabel: "Rezolvă numele de dispozitiv duplicat",
  },

  clipboard: {
    title: "Clipboard",
    empty: "Clipurile de pe celelalte dispozitive apar aici. Atinge pentru a copia.",
    from: (label: string): string => `de la ${label}`,
    copy: "Copiază în clipboard",
    copied: "Copiat",
    reveal: "Afișează",
    hide: "Ascunde",
    sensitiveChip: "Sensibil",
    cloudChip: "prin tokenul tău API",
    maskedHint: "Conținut ascuns — apasă Afișează pentru a-l vedea",
    sendPlaceholder: "Lipește textul de trimis…",
    sendCta: "Trimite",
    sendSensitiveToggle: "Marchează drept sensibil",
    sendSuccess: (n: number): string =>
      `Trimis către ${n} ${selectPlural("ro", n, {
        one: "dispozitiv",
        few: "dispozitive",
        other: "de dispozitive",
      })}.`,
    sendPartial: (n: number, total: number): string =>
      `Au fost contactate ${n}/${total} ${selectPlural("ro", total, {
        one: "dispozitiv",
        few: "dispozitive",
        other: "de dispozitive",
      })}.`,
    sendNoDevices: "Niciun dispozitiv asociat către care să trimiți.",
    sendFailed: "Trimiterea a eșuat. Încearcă din nou.",
    sendEmpty: "Nimic de trimis.",
    dockExpand: "Afișează istoricul clipboardului",
    dockCollapse: "Restrânge",
    dockSendTitle: "Trimite către dispozitivele tale",
    dockFromClipboard: (chars: number): string =>
      `Din clipboard · ${chars} ${selectPlural("ro", chars, {
        one: "caracter",
        few: "caractere",
        other: "de caractere",
      })}`,
    dockSend: (n: number): string => `Trimite către ${n}`,
    dockCancel: "Anulează",
    dockRecentHeading: "Recente",
    dockEmptyClipboard: "Clipboardul tău este gol.",
    shareTitle: "Trimite către dispozitivele tale",
    shareReview: "Verifică ce trimiți. Ajunge la dispozitivele tale asociate, criptat end-to-end.",
    shareSend: "Trimite",
    shareCancel: "Anulează",
    shareNotPaired: "Asociază mai întâi un dispozitiv pentru a trimite text partajat.",
    shareOpenApp: "Deschide Reflecto",
  },
  install: {
    title: "Instalează Reflecto",
    body: "Adaugă pe ecranul principal pentru acces mai rapid.",
    cta: "Instalează",
    dismissAria: "Închide solicitarea de instalare",
  },
  common: {
    loading: "Se încarcă",
  },
  errors: {
    notPaired: "Neasociat",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} pe ${os}`,
    webFallback: "Browser web",
  },

  palette: {
    placeholder: "Scrie o comandă…",
    noResults: "Nicio comandă potrivită",
    ariaLabel: "Paletă de comenzi",
    clearAll: "Șterge toate notificările",
    glance: "Rezumat Glance",
    cancelSnooze: "Anulează amânarea",
    copyClip: "Copiază ultimul clip primit",
  },
} satisfies MessagesShape;
